import { useEffect, useState } from "react";

/**
 * Hook to listen for grid row count updates.
 *
 * PERFORMANCE OPTIMIZATION:
 * This hook (and the associated 'publish' utility) implements a Pub/Sub pattern
 * using CustomEvents to sync the grid's filtered row count with the UI.
 *
 * WHY NOT USE STANDARD REACT STATE IN THE PARENT?
 * If the row count is stored in the parent component's state, every filter change
 * (e.g., every keystroke in the search bar) triggers a full re-render of the parent.
 * Since the parent usually contains the AgGrid instance itself, this causes
 * unnecessary re-evaluations of grid props and can lead to UI lag.
 *
 * BY USING EVENTS:
 * We decouple the "Publisher" (the Grid's onFilterChanged callback) from the
 * "Subscriber" (the Count display). Only the tiny component using this hook
 * re-renders, while the heavy parent component remains static.
 *
 * @param gridId A unique identifier for the specific grid to avoid cross-grid interference.
 */
export const useGridRowCount = (gridId: string) => {
  const [count, setCount] = useState(0);

  useEffect(() => {
    const eventName = `ag-grid-count-${gridId}`;
    const handler = (e: any) => {
      if (e instanceof CustomEvent) {
        setCount(e.detail.count);
      }
    };

    window.addEventListener(eventName, handler);
    return () => window.removeEventListener(eventName, handler);
  }, [gridId]);

  return count;
};

/**
 * Utility to trigger the count update from the grid
 */
export const publishGridRowCount = (gridId: string, count: number) => {
  const eventName = `ag-grid-count-${gridId}`;
  window.dispatchEvent(new CustomEvent(eventName, { detail: { count } }));
};
